import { useMemo, useState } from 'react'
import { InfiniteData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { searchOrders, traceOrder } from '@/console/api/orders'
import { toPanelError } from '@/console/api/errors'
import { DevConsoleOrderPage, DevConsoleOrderRow, DevConsoleOrderTrace, OrderSearchParams, OrderTraceKey } from '@/console/api/types'
import { PanelState, toPanelState } from '@/console/state/panelState'

/**
 * LOGISPRD-13034 · 13035 — F-OPT 조회 · Trace 조회를
 * `useState` + `seqRef` 수기 관리에서 콘솔 선례(`data/holds.ts`)와 같은 `@tanstack/react-query`
 * 규약으로 옮긴다. 두 훅이 복제하고 있던 "늦은 응답 폐기" 로직도 이 전환으로 한 군데(react-query
 * 자체)로 합쳐진다.
 *
 * **같은 조건 재조회 처리 — nonce 를 queryKey 에 포함한다(구현자 판단).** `data/holds.ts` 선례는
 * `enabled:false` 고정 + `refetch()` 인데, 여기서는 그 방식을 그대로 쓰지 않았다: `useOrderSearch`
 * 는 `useInfiniteQuery` 라 `refetch()` 를 부르면 **지금까지 쌓인 페이지 전부**(예: 더 보기로 2페이지째
 * 까지 받은 상태)를 각 페이지가 원래 쓰던 `pageParam` 그대로 다시 부른다 — "조회" 버튼은 항상
 * 첫 페이지부터 다시 보여줘야 하는데(조회는 새 조건의 첫 조회이지 커서를 이어가는 더 보기가 아니다), `refetch()` 로는 이 리셋을
 * 표현할 수 없다. 그래서 `search()`/`run()` 을 부를 때마다 내부 상태(`request`)에 1씩 증가하는
 * `nonce` 를 실어 **매번 새 queryKey 를 만든다** — react-query 입장에서는 완전히 새 쿼리라
 * `initialPageParam` 부터 다시 시작해 첫 페이지만 남는다. queryKey 앞부분은 여전히
 * `devConsoleOrdersQueryKeys.search/trace` 를 그대로 쓰므로 도메인별 `queryKeys` 관례에서 벗어나지
 * 않는다.
 *
 * **늦은 응답이 화면을 덮지 않는 지점(react-query 가 막아 주는 부분).** 조건을 바꿔(또는 같은 조건으로)
 * 다시 조회하면 `nonce` 가 바뀌어 컴포넌트가 구독하는 queryKey 도 바뀐다. 앞서 나간 요청은 옛 key 로
 * 캐시에 쓰이고, 화면은 오직 "지금 nonce 의 queryKey" 만 읽으므로 늦게 도착해도 반영되지 않는다 —
 * `seqRef` 로 직접 비교하던 것을 react-query 의 활성 쿼리 구독 자체가 대신한다. `loadMore`(더 보기)도
 * 마찬가지로 같은 활성 queryKey 안에서만 `fetchNextPage` 하므로, 조회 도중 조건이 바뀌면 그 순간의
 * `fetchNextPage` 응답도 옛 key 로 떨어져 무시된다.
 */
export const devConsoleOrdersQueryKeys = {
  all: ['console', 'dev-console-orders'] as const,
  search: (params: OrderSearchParams) => [...devConsoleOrdersQueryKeys.all, 'search', params] as const,
  trace: (key: OrderTraceKey) => [...devConsoleOrdersQueryKeys.all, 'trace', key] as const,
}

const IDLE_SEARCH_KEY = [...devConsoleOrdersQueryKeys.all, 'search', 'idle'] as const
const IDLE_TRACE_KEY = [...devConsoleOrdersQueryKeys.all, 'trace', 'idle'] as const

export type OrderSearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; kind: 'forbidden' | 'other'; message: string }
  // nextLastId 는 화면이 쓰지 않는다 — 다음 커서 유무는 hasNext(query.hasNextPage) 하나로만 본다.
  // 값 자체는 디버깅용으로 남겨 둔다(더 보기 요청이 실제로 어떤 커서를 물었는지 상태만으로 알 수 있게).
  | { status: 'ready'; items: DevConsoleOrderRow[]; hasNext: boolean; nextLastId: number | null; loadingMore: boolean; moreError: string | null }

type SearchRequest = { params: OrderSearchParams; nonce: number }

/**
 * `useInfiniteQuery` 의 몇몇 필드만 구조적으로 받는다 — `panelState.ts` 의 `toPanelState` 와 같은
 * 이유(테스트에서 react-query 객체를 통째로 흉내 내지 않아도 되게).
 *
 * H4 후속(4차 리뷰, LOGISPRD-13034) — `items` 는 인자로 받는다. 이 함수 자신은 순수 함수라 안에서
 * `useMemo` 를 부를 수 없다(훅 이름 규칙 위반 · rules-of-hooks 위반). 대신 `useOrderSearch` 가
 * `query.data?.pages` 를 의존성으로 `items` 를 먼저 메모해서 넘긴다 — `pages` 가 그대로면 여기서도
 * 같은 `items` 참조를 그대로 실어 돌려준다. `OrderSearchPanel` 의 `rows`/`columns` useMemo 가 이
 * 참조 안정성에 기대므로, 여기서 참조가 매번 바뀌면 그 useMemo 는 조회 폼 keystroke 마다 다시
 * 계산된다(원래 finding — `pages.flatMap(...)` 이 매 호출 새 배열을 만들었다).
 */
const toOrderSearchState = (
  query: {
    isFetching: boolean
    isFetchingNextPage: boolean
    isError: boolean
    isFetchNextPageError: boolean
    hasNextPage: boolean
    error: unknown
    data: InfiniteData<DevConsoleOrderPage> | undefined
  },
  items: DevConsoleOrderRow[],
): OrderSearchState => {
  // isFetchingNextPage 는 더 보기 전용 로딩이라 전체 화면 loading 으로 올리지 않는다(더 보기
  // 버튼 자체의 "가져오는 중…" 문구가 그 역할을 한다).
  if (query.isFetching && !query.isFetchingNextPage) {
    return { status: 'loading' }
  }

  if (query.data) {
    const pages = query.data.pages
    const last = pages[pages.length - 1]
    return {
      status: 'ready',
      items,
      // 서버 플래그(last.hasNext)를 그대로 올리지 않는다 — "더 보기" 버튼 표시와 loadMore 가드가
      // 서로 다른 값을 보면(예: hasNext:true 인데 nextLastId:null) 버튼은 보이는데 눌러도 반응이
      // 없는 증상이 난다. query.hasNextPage(getNextPageParam 이 undefined 를 돌려주면 false)
      // 하나로 판단 출처를 모은다.
      hasNext: query.hasNextPage ?? false,
      nextLastId: last.nextLastId,
      loadingMore: query.isFetchingNextPage,
      // 새 더 보기를 시작하면(isFetchingNextPage) moreError 를 지운다 — 재시도 중인데 이전 실패
      // 배너가 계속 떠 있으면 사용자가 "다시 시도했지만 또 실패했다"로 오해한다. v5 는 실패한
      // fetchNextPage 의 error 를 다음 fetchNextPage 가 성공/실패로 끝나기 전까지 쿼리 상태에 남겨
      // 두므로(data 는 유지한 채 error 만 노출), isFetchingNextPage 를 우선 본다.
      moreError: query.isFetchingNextPage ? null : query.isFetchNextPageError ? toPanelError(query.error).message : null,
    }
  }

  if (query.isError) {
    return { status: 'error', ...toPanelError(query.error) }
  }

  return { status: 'idle' }
}

/**
 * LOGISPRD-13034 — F-OPT 주문 조회. 전체 건수 없이 `nextLastId` 커서로 이어 붙인다
 * (`useInfiniteQuery` · `getNextPageParam`). `search()` 는 항상 커서 없는 첫 페이지부터 다시
 * 시작한다 — 들어온 `params.lastId` 는 무시한다(조회는 새 조건의 첫 조회이지 이어보기가 아니다).
 */
export const useOrderSearch = (): { state: OrderSearchState; search: (params: OrderSearchParams) => void; loadMore: () => void } => {
  const [request, setRequest] = useState<SearchRequest | null>(null)

  const query = useInfiniteQuery<DevConsoleOrderPage, unknown, InfiniteData<DevConsoleOrderPage>, readonly unknown[], number | undefined>({
    queryKey: request ? [...devConsoleOrdersQueryKeys.search(request.params), request.nonce] : IDLE_SEARCH_KEY,
    queryFn: ({ pageParam }) => searchOrders({ ...(request as SearchRequest).params, lastId: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (last) => (last.hasNext ? (last.nextLastId ?? undefined) : undefined),
    enabled: request !== null,
  })

  const search = (params: OrderSearchParams): void => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { lastId: _ignored, ...base } = params
    setRequest((prev) => ({ params: base, nonce: (prev?.nonce ?? 0) + 1 }))
  }

  const loadMore = (): void => {
    if (!query.hasNextPage || query.isFetchingNextPage) return
    query.fetchNextPage().catch(() => undefined)
  }

  // H4 후속(4차 리뷰, LOGISPRD-13034) — `query.data.pages` 가 그대로면 `items` 도 같은 배열
  // 참조를 돌려준다. 이게 없으면(예: `pages.flatMap(...)` 을 렌더마다 새로 부르면) 화면
  // (`OrderSearchPanel`)의 `useMemo(..., [items, ...])` 가 조회 폼 keystroke 마다 매번 다시
  // 계산돼 메모이제이션이 이름만 있고 효과가 없다. 의존성은 `query.data?.pages` 하나만 본다 —
  // react-query 는 페이지가 늘거나(더 보기) queryKey 가 바뀔 때(재조회)만 `data`(따라서
  // `data.pages`)를 새로 만든다.
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data?.pages])

  return { state: toOrderSearchState(query, items), search, loadMore }
}

type TraceRequest = { key: OrderTraceKey; nonce: number }

/** LOGISPRD-13035 — Trace 조회 상태. 다시 조회하면 앞선 요청의 늦은 응답은 버린다. 자동 재시도 없음. */
export const useOrderTrace = (): { state: PanelState<DevConsoleOrderTrace>; run: (key: OrderTraceKey) => void } => {
  const [request, setRequest] = useState<TraceRequest | null>(null)

  const query = useQuery<DevConsoleOrderTrace, unknown, DevConsoleOrderTrace, readonly unknown[]>({
    queryKey: request ? [...devConsoleOrdersQueryKeys.trace(request.key), request.nonce] : IDLE_TRACE_KEY,
    queryFn: () => traceOrder((request as TraceRequest).key),
    enabled: request !== null,
  })

  const run = (key: OrderTraceKey): void => {
    setRequest((prev) => ({ key, nonce: (prev?.nonce ?? 0) + 1 }))
  }

  // Trace 는 단일 객체 응답이라 "비어 있음" 개념이 없다 — isEmpty 를 항상 false 로 둬 기존 훅처럼
  // idle · loading · error · ready 4 상태만 낸다(empty 는 TraceTab 이 idle 을 자기 문구로 바꿔 흉내낸다).
  return { state: toPanelState(query, '', () => false), run }
}
